import { getUserAttributes } from '@mb/lib';
import { handleSystemErrorReport } from '@mb/lib/utils';
import { isPartnersPage, getPartnersPageQuery } from './helpers';
import { AppFiltersProps, FilterData, FilterDataValues } from '../types';

export const splittedCategoriesFields = [
  'categories',
  'application_filter',
  'products',
  'environmental_benefits_filter',
  'taxonomy',
];

interface DividerData {
  field: string;
  sign: string;
  signCount: number;
}

export const levelDividerByFacet: Record<string, DividerData> = {
  categories: { field: 'categories', sign: '>', signCount: 1 },
  application_filter: { field: 'application_filter', sign: '/', signCount: 1 },
  products: { field: 'products', sign: '>', signCount: 1 },
  environmental_benefits_filter: {
    field: 'environmental_benefits_filter',
    sign: '/',
    signCount: 1,
  },
  taxonomy: { field: 'taxonomy', sign: '>', signCount: 2 },
};
export const splittedCategoriesDivider = /\//g;
const dividers = [/\s?>\s?/g, splittedCategoriesDivider];

export const SWITCHES_FIELDS = ['is_gps_compliant'];

export function splitSwitches(
  list: FilterData[],
  redundantFacets: string[] = [],
) {
  const switches = [];
  const lists = [];

  for (const item of list) {
    if (SWITCHES_FIELDS.includes(item.field || '')) {
      const activeValue = item.values?.find((facet) => facet.label === 'true');
      const facetData = {
        ...activeValue,
        field: item.field,
        id: `filter.${item.field}=true`,
        label: item.label?.split('_parent')?.[0],
        value: 'true',
      };
      switches.push(facetData);
    } else {
      // Apply filter logic directly here
      const filterField = String(item?.field).toLowerCase();
      const hasValues = (item?.values ?? []).length >= 1;
      const isNotRedundant = !redundantFacets.includes(filterField);
      const isVisible = item?.visible;

      if (hasValues && isNotRedundant && isVisible) {
        lists.push(item);
      }
    }
  }

  return [switches, lists];
}

export function getFiltersWithoutCheckboxValues(list: AppFiltersProps) {
  return Object.fromEntries(
    Object.entries(list).filter(
      ([key, value]) => !SWITCHES_FIELDS.includes(value.field || ''),
    ),
  );
}

export const isDivider = (label: string) => {
  return dividers.find((divider) => new RegExp(divider).test(label));
};

export function createMultiLevelFilters(
  values: FilterDataValues[],
  field = '',
  multiple: string | undefined = undefined,
) {
  const result = values.reduce(
    (acc: Record<string, any>, filter: FilterData) => {
      const divider = isDivider(filter.label);
      if (divider) {
        if (
          divider === splittedCategoriesDivider &&
          !splittedCategoriesFields.includes(field)
        ) {
          acc[filter.label] = {
            items: [],
            count: filter.count ?? 0,
            multiple: multiple,
            field,
          };
          return acc;
        }

        const [parent, child, grandChild] = filter.label.split(divider);
        if (grandChild != null) {
          // Ignore grandchild, since logic is not handling 3 level filters
          return acc;
        }

        const updatedFilter = { ...filter, parent };

        const newItem = [child, updatedFilter];
        const parentItem = values.find(({ label }) => label === parent);

        if (acc[parent] == null) {
          acc[parent] = {};
        }
        if (acc[parent].items == null) {
          acc[parent].items = [];
        }

        acc[parent].field = field;
        acc[parent]?.items?.push(newItem);

        const parentCount = Number(parentItem?.count);

        acc[parent].count = !Number.isNaN(parentCount)
          ? parentCount
          : Number(filter.count) || 0;
      } else {
        if (filter.high != null || filter.low != null) {
          acc[filter.label] = {
            items: [],
            count: filter.count ?? 0,
            originFilter: filter,
          };
          if (filter.high != null) {
            acc[filter.label].high = filter.high;
          }
          if (filter.low != null) {
            acc[filter.label].low = filter.low;
          }
        } else {
          acc[filter.label] = {
            id: `filter.${field}=${filter.label}`,
            items: [],
            count: filter.count ?? 0,
            multiple: multiple,
            field,
            active: filter.active,
          };
        }
      }

      return acc;
    },
    // TODO: refactor without type assertion, or add comment
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<string, any>,
  );

  return Object.entries(result);
}

export function decodeFilterValue(value: string | boolean) {
  //weird format, this should be temporary and is here to support old postback style
  //when filters/queries come from header buttons/categories
  if (typeof value === 'boolean') return String(value);
  return value
    ?.replace(/(\#)/g, '')
    ?.replace(/(\$2520)/g, ' ')
    ?.replace(/(\$2526)/g, '&')
    ?.replace(/(\$253E)/g, '>')
    ?.replace(/(\$252F)/g, '/');
}

export function encodeFilterValue(value: string) {
  return value
    ?.replace(/(\s)/g, '$2520')
    ?.replace(/(\&)/g, '$2526')
    ?.replace(/(\>)/g, '$253E')
    ?.replace(/(\/)/g, '$252F');
}

// Helper functions to extract specific parts of the URL
const extractSearchChunk = (href: string) => {
  return href?.split('search')?.[1] || '';
};

const extractChunksByType = (searchChunk: string, chunkType: string) => {
  return (
    searchChunk?.split('/')?.filter((chunk) => chunk.includes(chunkType)) || []
  );
};

// Process specific filter types
const processAbrasionFilter = (key: string) => {
  const abrasionResultFilter = key.split('&');
  const abrasionResultLow = abrasionResultFilter?.[0]?.split('=')?.[1];
  const abrasionResultHigh = abrasionResultFilter?.[1]?.split('=')?.[1];

  if (abrasionResultLow != null && abrasionResultHigh != null) {
    return {
      field: 'abrasion_result',
      id: key,
      low: parseInt(abrasionResultLow || '0'),
      high: parseInt(abrasionResultHigh || '0'),
      label: `${abrasionResultLow} +`,
    };
  }
  return null;
};

const processPriceFilter = (key: string) => {
  const priceFilters = key.split('&');
  const lowPrice = priceFilters?.[0]?.split('=')?.[1];
  const highPrice = priceFilters?.[1]?.split('=')?.[1];
  const highPriceNumber = parseInt(highPrice);

  if (lowPrice != null && highPrice != null) {
    return {
      field: 'price_sign_filter',
      id: key,
      low: parseInt(lowPrice || '0'),
      high: parseInt(highPrice || '0'),
      label:
        !isNaN(highPriceNumber) && highPriceNumber > 1
          ? `${'$'.repeat(highPriceNumber)} or Less`
          : '$',
    };
  }
  return null;
};

const processStandardFilter = (key: string, field: string, value: string) => {
  return {
    field,
    id: key,
    label: value,
    value,
  };
};

// Process a single filter chunk
const processFilterChunk = (filterChunk: string) => {
  try {
    const subFilterChunks = decodeURIComponent(filterChunk).split(':');
    if (subFilterChunks.length !== 3) return null;

    const filter = subFilterChunks[0];
    const field = subFilterChunks[1];
    const value = decodeFilterValue(subFilterChunks[2]);
    const key = `${filter}.${field}=${value}`;

    if (key.includes('abrasion_result')) {
      return { key, filter: processAbrasionFilter(key) };
    } else if (key.includes('price_sign_filter')) {
      return { key, filter: processPriceFilter(key) };
    } else {
      return { key, filter: processStandardFilter(key, field, value) };
    }
  } catch (e) {
    console.error('Error processing filter chunk:', e);
    return null;
  }
};

// Extract view filter from URL
const extractViewFilter = (searchChunk: string) => {
  const viewChunk = extractChunksByType(searchChunk, 'view');
  if (viewChunk.length > 0) {
    const viewFilterValue = viewChunk[0]?.split(':');
    if (viewFilterValue?.[1]) {
      return viewFilterValue[1]?.split('#')?.[0];
    }
  }
  return undefined;
};

// Extract visibility filter from URL
const extractVisibilityFilter = (searchChunk: string) => {
  const visibilityChunk = extractChunksByType(searchChunk, 'filter:visibility');
  if (visibilityChunk.length > 0) {
    const viewFilterValue = visibilityChunk[0]?.split(':');
    if (viewFilterValue?.[2]) {
      const visibilityValue = viewFilterValue[2]?.replaceAll('#', '');
      return visibilityValue === 'Catalog' ? 'individual' : 'families';
    }
  }
  return undefined;
};

export const getInitialUrlData = () => {
  const initialAppFilters: AppFiltersProps = {};
  let initialViewFilter = undefined;

  try {
    const isPartnersRoute = isPartnersPage();
    const hrefChunks = isPartnersRoute
      ? getPartnersPageQuery()
      : decodeURIComponent(window.location.href);
    const searchChunk = extractSearchChunk(hrefChunks || '');

    if (searchChunk) {
      // Process filter chunks
      const filterChunks = extractChunksByType(searchChunk, 'filter');
      filterChunks.forEach((filterChunk) => {
        const result = processFilterChunk(filterChunk);
        if (result && result.filter) {
          initialAppFilters[result.key] = result.filter;
        }
      });

      // Get view filter - try normal view first, then visibility filter
      initialViewFilter =
        extractViewFilter(searchChunk) || extractVisibilityFilter(searchChunk);
    }
  } catch (e) {
    handleSystemErrorReport(
      `getInitialUrlData Error: ${e.message}`,
      'getInitialUrlData',
    );
    console.error(e);
  }

  return { initialAppFilters, initialViewFilter };
};

export const getInitialIsActiveBrand = () => {
  const userData = getUserAttributes();
  return userData?.activeBrand?.rep_tools === '1';
};
